import { Component, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { Angulartics2 } from 'angulartics2';
import { AuthService } from 'app/user/auth.service';
import { DefaultService } from 'app/services/default.service';
import { EnvironmentService } from 'app/services/environment.sevice';
import {
  GeolocationService,
  Geolocation
} from 'app/services/geolocation.service';
import { getPolicyData, PolicyData } from 'app/services/policy.service';
import { settings, Settings } from 'app/core/settings';
import { camelCase } from 'app/core/case-transform';
import { validate } from 'app/user/form.functions';

interface UserRegistration {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  country: string;
  currency: string;
  language: string;
  timeZoneOffset: string;
  confirmationUrl: string;
  passwordChangeUrl: string;
  projectDetails: string;
  subscribeToEmails: boolean;
  accepted: boolean;
  hasTenant: boolean;
  services: ProductInterest;
  tosCheckbox: boolean;
  privacyAndCookie: boolean;
}

class ProductInterest {
  invoiceProcessing: boolean = true;
  invoiceServiceApi: boolean = true;
  hostedCloud: boolean = true;

  isSet(): boolean {
    return this.invoiceProcessing || this.invoiceServiceApi || this.hostedCloud;
  }

  toLead() {
    const products = [
      this.invoiceProcessing && 'FlexiCapture for Invoices Cloud',
      this.invoiceServiceApi && 'FlexiCapture for Invoices API Cloud',
      this.hostedCloud && 'FlexiCapture Cloud'
    ];
    return products.filter(product => !!product).join(';');
  }
}

interface Country {
  label: string;
  value: string;
  lowerCaseValue: string;
  code: string;
}

@Component({
  selector: 'register',
  templateUrl: 'register.component.html',
  styleUrls: ['register.component.scss']
})
export class RegisterComponent implements OnDestroy {
  error: string;

  errorDescription: string;

  processing: boolean;

  registration: UserRegistration;

  countries: Country[];

  filteredCountries: Country[];

  subscription: Subscription;

  policy: PolicyData;

  get settings(): Settings {
    return settings;
  }

  constructor(
    private _router: Router,
    private _authService: AuthService,
    private _angulartics2: Angulartics2,
    private _geolocationService: GeolocationService,
    defaultService: DefaultService,
    environmentService: EnvironmentService
  ) {
    this.policy = {
      privacyPolicyUrl: 'https://www.abbyy.com/privacy/?version=default',
      cookiePolicyUrl: 'https://www.abbyy.com/cookie-policy',
      type: 'ALL'
    };

    this.registration = {
      firstName: null,
      lastName: null,
      email: null,
      company: null,
      phone: null,
      country: null,
      currency: defaultService.currency,
      language: defaultService.language,
      timeZoneOffset: environmentService.timeZoneOffset,
      confirmationUrl:
        environmentService.mainServer + '/password/set/%7Btoken%7D',
      passwordChangeUrl:
        environmentService.mainServer + '/password/change/%7Btoken%7D',
      projectDetails: null,
      subscribeToEmails: false,
      accepted: true,
      hasTenant: false,
      services: new ProductInterest(),
      tosCheckbox: false,
      privacyAndCookie: false
    };
    this.countries = this._geolocationService
      .getCountriesNames()
      .map(country => ({
        label: country.name,
        value: country.name,
        code: country.code,
        lowerCaseValue: country.name.toLowerCase()
      }));

    this.filteredCountries = this.countries;

    this.subscription = this._geolocationService
      .locate()
      .subscribe(geolocation => {
        this._setLocation(geolocation)
      });
  }

  isLengthMaxProjectDetails(): boolean {
    return (
      this.registration.projectDetails &&
      this.registration.projectDetails.length === 1024
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  countriesSelected(value) {
    this.policy = getPolicyData(value.code);
  }

  onFilterInputChanged(term: string) {
    this.filteredCountries = this.countries.filter(country =>
      country.lowerCaseValue.startsWith(term)
    );
  }

  onSelectClosed() {
    this.filteredCountries = [...this.countries];
  }

  validate(form: NgForm): void {
    validate(form);
  }

  onHideError(): void {
    this.error = null;
  }

  onSubmit(form: NgForm): void {
    if (form.invalid) {
      return;
    }

    this.error = null;
    this.errorDescription = null;
    this.processing = true;

    this._authService.register(this.registration).subscribe(
      () => this._onSuccess(),
      response => this._onError(response)
    );
  }

  private _onSuccess(): void {
    const isUSGroupCountry = ['CA_JP', 'US'].includes(this.policy.type);

    const lead = {
      firstName: this.registration.firstName,
      lastName: this.registration.lastName,
      email: this.registration.email,
      company: this.registration.company,
      phone: this.registration.phone,
      country: this.registration.country,
      language: this.registration.language,
      projectDetails: this.registration.projectDetails,
      hasTenant: this.registration.hasTenant,
      unsubscribed: !this.registration.subscribeToEmails,
      projectDescriptionTemp: this.registration.projectDetails,
      productInterestTemp: this.registration.services.toLead(),
      GDPRConsentForProcessing: isUSGroupCountry
        ? null
        : this.registration.privacyAndCookie,
      GDPRConsentNotes: isUSGroupCountry
        ? null
        : `User provided consent by filling out FlexiCapture Cloud Registration form at ${
            window.location
          } ${new Date().toUTCString()}`,
      optIn: this.registration.subscribeToEmails,
      isTest: this._isTestEmail(this.registration.email)
    };

    this._angulartics2.setUserProperties.next(lead);
    this._router.navigateByUrl('/');
  }

  private _isTestEmail(email: string): boolean {
    const testDomains = {
      '@abbyy.com': true,
      '@mailforspam.com': true,
      '@mailinator.com': true,
      '@mfsa.ru': true,
      '@sharklasers.com': true,
      '@mfsa.info': true
    };
    const domain = email.substring(email.indexOf('@'));
    return domain in testDomains;
  }

  private _sendRegistrationSuccess(email: string): void {
    this._angulartics2.setUsername.next(email);
    this._angulartics2.eventTrack.next({
      action: 'RegistrationSuccess',
      properties: {
        event: 'RegistrationSuccess'
      }
    });
  }

  private _onError(response): void {
    if (response.status === 400) {
      const responseJson = response.json();
      this.error = camelCase(responseJson.error);
      this.errorDescription = this._getErrorDescription(responseJson);
    }
    this.processing = false;
  }

  private _getErrorDescription(responseJson): string {
    const modelState = responseJson.modelState;
    if (!modelState) {
      return responseJson.errorDescription;
    }

    return Object.keys(modelState)
      .map(fieldName => modelState[fieldName].join('\n'))
      .join('\n');
  }

  private _setLocation({ country }: Geolocation): void {
    if (this.registration.country) {
      return;
    }

    this.registration.country = country.name;
    this.policy = getPolicyData(country.code);
  }
}
